import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Button,
  Typography,
  Stack,
  Paper,
} from "@mhome/ui";
import CloseIcon from "@mui/icons-material/Close";
import OAuthFlow from "./connect/oauth-flow";
import DynamicMdxRenderer from "../common/mdx";
import { completeConnectIntegration } from "../service/integration-service";
import { useRecoilState, useRecoilValue } from "recoil";
import { platformState, safeAreaInsetsState } from "../global-state";
import { useAppInterface } from "../app/app-interface-provider";
import { useWebSocket } from "../ws/websocket";
import { useMediaQuery } from "@mui/material";
import { useComponentBackgroundColor } from "../common/adaptive-theme-provider";

const ConnectDialog = ({
  open,
  integrationId,
  integrationName,
  connectInfo,
  onClose,
  onSuccess,
  onFailure,
}) => {
  const getComponentBgColor = useComponentBackgroundColor();
  const { sendAndWaitWs } = useWebSocket();
  const [platform] = useRecoilState(platformState);
  const appInterface = useAppInterface();
  const [currentGuideIndex, setCurrentGuideIndex] = useState(0);
  const [inputValues, setInputValues] = useState(new Map());
  const [canProceed, setCanProceed] = useState(true);
  const safeAreaInsets = useRecoilValue(safeAreaInsetsState);
  const isSmallScreen = useMediaQuery("@media (max-width:959px)");

  useEffect(() => {
    inputValues.clear();
    setCurrentGuideIndex(0);
    validateCurrentPage(0);
  }, [open]);

  const handleComplete = async () => {
    console.debug("handleComplete");
    onClose();
    if (connectInfo.oauthConnect) {
      onSuccess();
    } else if (connectInfo.customConnect) {
      const resp = await completeConnectIntegration(
        sendAndWaitWs,
        integrationId,
        { data: Object.fromEntries(inputValues) }
      );
      if (resp && !resp.error && resp.success) {
        onSuccess();
      } else {
        onFailure();
      }
    }
  };

  const handleFailure = () => {
    onClose();
    onFailure();
  };

  const handleInputChange = (varName, value) => {
    console.debug(varName, value);
    inputValues.set(varName, value);
    // Check if the current MDX has any TextInput and if it has value
    validateCurrentPage(currentGuideIndex);
  };

  const validateCurrentPage = (index) => {
    if (!connectInfo?.customConnect?.guides?.[index]) {
      return;
    }

    const currentMdx = connectInfo.customConnect.guides[index];

    // Find all TextInput components and their var attributes
    const textInputRegex = /<TextInput[^>]*var="([^"]+)"[^>]*>/g;
    const textInputMatches = [...currentMdx.matchAll(textInputRegex)];

    if (textInputMatches.length > 0) {
      // Check if all TextInput components have values
      const allInputsHaveValues = textInputMatches.every((match) => {
        const varName = match[1]; // Get the var name from the regex capture group
        return !!inputValues.get(varName) || inputValues.get(varName) === false;
      });

      setCanProceed(allInputsHaveValues);
    } else {
      // If there are no TextInput components, user can proceed
      setCanProceed(true);
    }
  };

  const handleGoogleHomeConnect = () => {
    if (appInterface.isAvailable()) {
      appInterface.connectGoogleHome();
      onClose();
      onSuccess();
    }
  };

  const handleNext = async () => {
    if (currentGuideIndex < connectInfo.customConnect.guides.length - 1) {
      const index = currentGuideIndex + 1;
      setCurrentGuideIndex(index);
      validateCurrentPage(index);
    } else {
      await handleComplete();
    }
  };

  // Check if we should show Google Home connect button
  const shouldShowGoogleHomeConnect = () => {
    return (
      integrationId === "mlinkGoogleHome.device" &&
      appInterface.isAvailable() &&
      platform.source === "app" &&
      platform.os === "android"
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={isSmallScreen}
      variant={isSmallScreen ? undefined : "drawer"}
      PaperProps={{
        style: {
          width: isSmallScreen ? "100%" : "600px",
          marginTop: isSmallScreen ? 0 : `${safeAreaInsets.top}px !important`,
          marginBottom: isSmallScreen
            ? 0
            : `${safeAreaInsets.bottom}px !important`,
          height: isSmallScreen
            ? "100vh"
            : `calc(100vh - ${
                safeAreaInsets.top + safeAreaInsets.bottom
              }px) !important`,
          maxHeight: isSmallScreen
            ? "100vh"
            : `calc(100vh - ${
                safeAreaInsets.top + safeAreaInsets.bottom
              }px) !important`,
          paddingTop: isSmallScreen ? `${safeAreaInsets.top}px` : 0,
          paddingBottom: isSmallScreen ? `${safeAreaInsets.bottom}px` : 0,
          paddingRight: `${safeAreaInsets.right}px`,
        },
      }}
    >
      <DialogTitle className="p-6 flex items-center gap-4 min-h-[48px]">
        <IconButton
          edge="start"
          color="inherit"
          onClick={onClose}
          aria-label="close"
          size="small"
        >
          <CloseIcon />
        </IconButton>
        <Typography variant="subtitle1" component="div" className="flex-1">
          {"Connect to " + integrationName}
        </Typography>
      </DialogTitle>
      <DialogContent className="p-4 flex flex-col gap-6">
        <div className="flex flex-col items-center justify-between">
          {connectInfo && connectInfo.oauthConnect ? (
            <>
              <Paper
                variant="outlined"
                className="p-4 w-full"
                style={{
                  backgroundColor: getComponentBgColor("hsl(var(--card))"),
                }}
              >
                <Typography variant="body1" className="mb-6">
                  {integrationName} connects to an external service. You will be
                  redirected to the service provider site for authorization.
                </Typography>
                <div className="mt-4">
                  {connectInfo && connectInfo.oauthConnect && (
                    <OAuthFlow
                      oAuthInfo={connectInfo.oauthConnect}
                      onSuccess={handleComplete}
                      onFailure={handleFailure}
                    />
                  )}
                </div>
              </Paper>
            </>
          ) : connectInfo && connectInfo.customConnect ? (
            <>
              <Paper
                variant="outlined"
                className="p-4 w-full flex-1 overflow-auto mb-2"
                style={{
                  backgroundColor: getComponentBgColor("hsl(var(--card))"),
                }}
              >
                <DynamicMdxRenderer
                  mdxContent={
                    connectInfo.customConnect.guides[currentGuideIndex]
                  }
                  onInputChange={handleInputChange}
                />
              </Paper>
              <Stack direction="row" spacing={2} className="w-full justify-end">
                {shouldShowGoogleHomeConnect() &&
                currentGuideIndex ===
                  connectInfo.customConnect.guides.length - 1 ? (
                  <Button
                    variant="contained"
                    disabled={!canProceed}
                    onClick={handleGoogleHomeConnect}
                    className="normal-case"
                    style={{ boxShadow: "none" }}
                  >
                    Connect
                  </Button>
                ) : (
                  <Button
                    variant="contained"
                    disabled={!canProceed}
                    onClick={handleNext}
                    className="normal-case"
                    style={{ boxShadow: "none" }}
                  >
                    {currentGuideIndex ===
                    connectInfo.customConnect.guides.length - 1
                      ? "Done"
                      : "Next"}
                  </Button>
                )}
              </Stack>
            </>
          ) : null}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ConnectDialog;
